import { Box, Checkbox, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { Field, FieldProps, useFormikContext } from 'formik'
import { DatePicker } from 'formik-mui-x-date-pickers'
import { useConfirm } from 'material-ui-confirm'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { VinciButton } from '~/components'
import { useResourceAutocomplete } from '~/components/ResourceAutocomplete/ResourceAutocomplete'
import { useAuth } from '~/hooks/useAuth'
import { useAircraftById } from '~/modules/aircraft/hooks'
import { CompanyForm } from '~/modules/company/components/CompanyForm'
import { createCompany } from '~/modules/company/data/companyApi'
import { ChecklistSelect } from '~/modules/inspection/components/ChecklistSelect'
import { CreateAuditMenu } from '~/modules/inspection/components/InspectionForm/CreateAuditMenu'
import { checkIsSupervisorUp } from '~/modules/inspection/utils/checkIsSupervisorUp'
import { UserForm } from '~/modules/user/components'
import { useUserData } from '~/modules/user/data/useData'
import { createUser } from '~/modules/user/data/userApi'
import { CompanyAssetForm } from '~/tmp/assetTypes/components/CompanyAssetForm'
import {
  CompanyAssetModal,
  companyAssetValidationSchema,
} from '~/tmp/assetTypes/components/CompanyAssetModal'
import { assetToCompanyAsset } from '~/tmp/assetTypes/functions'
import { sortByAlphabeticalOrder } from '~/utils/comparators/sortByAlphabeticalOrder'
import { removeEmptyValues } from '~/utils/removeEmptyValues'
import {
  IAircraft,
  ICompany,
  ICompanyAsset,
  IInspectionChecklist,
  initialAircraft,
  initialCompany,
  initialCustomAsset,
  initialUser,
  IUser,
} from '~/utils/types'
import { ICustomAssetInspectionSubmitValues } from '../create-custom-asset-audit'
import * as S from './CompanyAssetAuditForm.styles'

interface ICompanyAssetAuditFormProps {
  editing?: boolean
  onSave?: () => void
  onCreate?: () => void
}

export const CompanyAssetAuditForm: FC<ICompanyAssetAuditFormProps> = ({
  editing = false,
  onCreate,
  onSave,
}) => {
  const resourceAutocomplete = useResourceAutocomplete()
  const confirm = useConfirm()
  const { user } = useAuth()
  const { useListAllUsers } = useUserData()
  const users = useListAllUsers()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const aircraftId = searchParams.get('aircraft_id')

  const formik = useFormikContext<ICustomAssetInspectionSubmitValues>()
  const setFieldValue = useMemo(() => formik.setFieldValue, [formik.setFieldValue])

  const { data: selectedAircraft } = useAircraftById(aircraftId ?? '')

  const [useSelectedAircraft, setUseSelectedAircraft] = useState(false)

  useEffect(() => {
    if (aircraftId && selectedAircraft) {
      setUseSelectedAircraft(true)
      setFieldValue('aircraft', selectedAircraft)
    }
  }, [aircraftId, selectedAircraft, setFieldValue])

  const handleSelectChecklist = (checklist: IInspectionChecklist | null) => {
    if (checklist) {
      formik.setFieldValue('general_info.checklist', checklist)
    } else {
      formik.setFieldValue('general_info.checklist', null)
    }
  }

  const handleSelectCustomer = (customer: ICompany | null) => {
    formik.setFieldValue('aircraft', useSelectedAircraft ? selectedAircraft : undefined)
    formik.setFieldValue('general_info.checklist', undefined)
    formik.setFieldValue('general_info.inspection_company', undefined)

    formik.setFieldValue('general_info.inspector', undefined)
    formik.setFieldValue('general_info.additional_inspectors', undefined)
    if (customer) {
      formik.setFieldValue('general_info.customer', customer)

      setCurrentCustomer(customer)
    } else {
      formik.setFieldValue('general_info.customer', null)
      formik.setFieldValue('general_info.checklist', null)
      setCurrentCustomer(null)
    }
  }

  const [currentCustomer, setCurrentCustomer] = useState<ICompany | null>(
    formik.values.general_info?.customer ?? null,
  )
  const submitButtonDisabled = useMemo(
    () => !formik.dirty || Object.keys(formik.errors).length > 0,
    [formik.errors, formik.dirty],
  )

  const sortedUsers = useMemo(() => {
    if (!users.data) return []

    return users.data.sort((a, b) => sortByAlphabeticalOrder(a.name, b.name))
  }, [users])

  const handleCreateUser = async (user: IUser) => {
    const values = removeEmptyValues(user) as IUser

    return await createUser(values)
  }

  const handleSave = () => {
    confirm({
      description: 'Are you sure you want to save this audit?',
    }).then(() => formik.submitForm())
  }

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const isCreateAuditMenuOpen = Boolean(anchorEl)

  const handleClose = () => {
    setAnchorEl(null)
  }

  interface ICompanyAssetModalState {
    status: 'creating' | 'editing' | false
    values?: IAircraft
    initialAsCustom?: Partial<ICompanyAsset>
  }
  const [companyAssetModal, setCompanyAssetModal] = useState<ICompanyAssetModalState>({
    status: false,
    values: initialAircraft(),
  })

  return (
    <>
      <S.Section>
        <S.InputGroup>
          <Field name='general_info.customer'>
            {(fieldProps: FieldProps) =>
              resourceAutocomplete<ICompany>({
                resourcePath: 'company',
                resourceFilter: (company) =>
                  company._id === (user?.company as ICompany)._id ||
                  company.company_type.includes('customer') ||
                  company.company_type.includes('asset_manager') ||
                  company.company_type.includes('inspection_company'),
                resourceSortComparator: (a, b) => sortByAlphabeticalOrder(a.name, b.name),
                requestProps: {
                  initialText: t('Creating customer...'),
                  errorText: t('There was an error creating customer'),
                  successText: t('Customer successfully created!'),
                  createRequestFunction: createCompany,
                },
                renderOption: (props, option) => (
                  <Box component='li' {...props} key={option._id}>
                    <Box>
                      <Typography>{option.name}</Typography>
                      <Typography variant='caption'>Parent: {option.parent ?? 'None'}</Typography>
                    </Box>
                  </Box>
                ),
                matchValueOnBlur(value, options) {
                  return options.find((i) => (value ? i.name.includes(value) : false))
                },
                onSelectResource: (company) => handleSelectCustomer(company),
                modalProps: {
                  form: {
                    initialValues: initialCompany(),
                  },
                  title: t('Create Company'),
                  children: <CompanyForm />,
                },
                autocompleteProps: {
                  sx: {
                    minWidth: 200,
                    my: 1,
                    flex: 1,
                  },
                  disabled: editing,
                  primaryIdentifier: 'name',
                  label: `${t('Ordered by')} *`,
                  ...fieldProps,
                },
              })
            }
          </Field>
          <Field name='general_info.inspection_company'>
            {(fieldProps: FieldProps) =>
              resourceAutocomplete<ICompany>({
                resourcePath: 'company',
                requestProps: {
                  initialText: t('Creating audit company...'),
                  errorText: t('There was an error creating audit company'),
                  successText: t('Audit company successfully created!'),
                  createRequestFunction: createCompany,
                },
                resourceSortComparator: (a, b) => sortByAlphabeticalOrder(a.name, b.name),
                resourceFilter: (company: ICompany) =>
                  company._id === (user?.company as ICompany)._id ||
                  company._id === currentCustomer?._id ||
                  company.company_type?.includes('inspection_company'),
                modalProps: {
                  form: {
                    initialValues: initialCompany(),
                  },
                  title: t('Create Company'),
                  children: <CompanyForm />,
                },
                renderOption: (props, option) => (
                  <Box component='li' {...props} key={option._id}>
                    <Box>
                      <Typography>{option.name}</Typography>
                      <Typography variant='caption'>Parent: {option.parent ?? 'None'}</Typography>
                    </Box>
                  </Box>
                ),
                autocompleteProps: {
                  sx: {
                    minWidth: 200,
                    my: 1,
                    flex: 1,
                  },
                  disabled: editing,
                  primaryIdentifier: 'name',
                  label: `${t('Audit Company')} *`,
                  ...fieldProps,
                },
              })
            }
          </Field>
        </S.InputGroup>
        <CompanyAssetModal
          creating={companyAssetModal.status === 'creating'}
          open={companyAssetModal.status !== false}
          values={companyAssetModal.values}
          initialValuesAsCustom={companyAssetModal.initialAsCustom}
          onMutate={(newItem) => {
            formik.setFieldValue('aircraft', newItem)
          }}
          onClose={() =>
            setCompanyAssetModal({
              status: false,
            })
          }
        />
        <S.InputGroup>
          <Field name='aircraft'>
            {(fieldProps: FieldProps) =>
              resourceAutocomplete<any>({
                resourcePath: 'aircraft',
                requestParams: {
                  limit: 999,
                },
                resourceFilter(item) {
                  return item.aircraft_model?.asset_type?.name == 'Company'
                },
                resourceGetter(data) {
                  if (data instanceof Object && 'docs' in data) {
                    return (data.docs as IAircraft[]) || []
                  }

                  return []
                },
                requestProps: {
                  initialText: t('Creating asset...'),
                  errorText: t('There was an error creating asset. Please try again!'),
                  successText: t('Asset successfully created!'),
                  createRequestFunction: undefined,
                },
                onCreate: (initialString) => {
                  setCompanyAssetModal({
                    status: 'creating',
                    initialAsCustom: {
                      department_area: initialString ?? '',
                    },
                  })
                },
                renderOption: (props, option) => {
                  const translated = assetToCompanyAsset(option)
                  const assetLabel = `${translated.department_area} - ${translated.company_name}`

                  return (
                    <Box component='li' {...props} key={option._id}>
                      <Box>
                        <Typography>{assetLabel}</Typography>
                        <Typography variant='caption'>
                          Scope: {option?.owner?.name ?? 'None'}
                        </Typography>
                        <Typography variant='caption' ml={3}>
                          Asset Type: {option?.aircraft_model?.asset_type?.name ?? 'None'}
                        </Typography>
                      </Box>
                    </Box>
                  )
                },
                onSelectResource: (resource: IAircraft) => {
                  formik.setFieldValue(
                    editing ? 'general_info.checklist' : 'general_info.checklist_ref',
                    undefined,
                  )
                  if (resource.manufacture_date) {
                    formik.setFieldValue('general_info.yom', resource.manufacture_date)
                    formik.setFieldValue('general_info.dom', resource.manufacture_date)
                  }
                },
                matchValueOnBlur: (value: string, options) =>
                  options.find((a) => {
                    return value ? a.serial_number.includes(value) : false
                  }),
                modalProps: {
                  form: {
                    initialValues: initialCustomAsset(),
                    validationSchema: companyAssetValidationSchema,
                  },
                  title: t('Create Asset'),
                  children: <CompanyAssetForm />,
                },
                autocompleteProps: {
                  sx: {
                    minWidth: 200,
                    my: 1,
                    flex: 1,
                  },
                  primaryIdentifier: 'serial_number',
                  optionLabelGetter: (item: IAircraft) => {
                    if (!item) {
                      return ''
                    }
                    const translated = assetToCompanyAsset(item)

                    return `${translated.department_area} - ${translated.company_name} (${
                      (item?.owner as ICompany)?.name ?? '-'
                    })`
                  },
                  disabled: !currentCustomer || useSelectedAircraft,
                  label: `${t('Company Asset')} *`,
                  error: formik.touched.aircraft && Boolean(formik.errors.aircraft),
                  helperText: formik.touched.aircraft && formik.errors.aircraft,
                  ...fieldProps,
                },
              })
            }
          </Field>
        </S.InputGroup>
        <S.SectionDivider />
        <ChecklistSelect
          aircraftModelId={formik.values.aircraft?._id}
          customerId={formik.values.general_info.customer?._id}
          value={
            formik.values.general_info.checklist ??
            formik.values.general_info.checklist_ref ??
            undefined
          }
          onChange={(newValue) => {
            const fieldName = editing ? 'general_info.checklist' : 'general_info.checklist_ref'
            formik.setFieldValue(fieldName, newValue)
            handleSelectChecklist(newValue ?? null)
          }}
          placeholder={`${t('Checklist')} *`}
          label={t('Checklist')}
          disabled={editing}
          error={
            formik.touched?.general_info?.checklist_ref &&
            Boolean(formik.errors.general_info?.checklist_ref)
          }
          helperText={
            formik.touched?.general_info?.checklist_ref &&
            (formik.errors.general_info?.checklist_ref as string)
          }
        />
      </S.Section>
      <S.Section>
        <S.InputGroup>
          <Field name='general_info.inspector'>
            {(props: FieldProps) =>
              resourceAutocomplete<IUser>({
                resourcePath: 'user',
                resourceFilter: (user: IUser) =>
                  checkIsSupervisorUp(user) &&
                  ((user?.company as ICompany)?._id === currentCustomer?._id ||
                    (user?.company as ICompany)?._id ===
                      formik?.values?.general_info?.inspection_company?._id),
                resourceSortComparator: (a, b) => sortByAlphabeticalOrder(a.name, b.name),
                requestProps: {
                  initialText: t('Creating user...'),
                  errorText: t('There was an error creating user. Please try again!'),
                  successText: t('User successfully created!'),
                  createRequestFunction: handleCreateUser,
                },
                modalProps: {
                  form: {
                    initialValues: { ...initialUser(), profile: 'inspector' },
                  },
                  title: t('Create User'),
                  children: <UserForm />,
                },
                autocompleteProps: {
                  sx: {
                    minWidth: 200,
                    my: 1,
                    flex: 1,
                  },
                  primaryIdentifier: 'name',
                  label: `${t('Lead Auditor/Supervisor')} *`,
                  error:
                    formik.touched.general_info?.inspector &&
                    Boolean(formik.errors.general_info?.inspector),
                  helperText:
                    formik.touched.general_info?.inspector && formik.errors.general_info?.inspector,
                  ...props,
                },
              })
            }
          </Field>

          <FormControl sx={{ flex: 1, m: 1 }}>
            <InputLabel id='additional-inspectors-label'>{t('Additional Auditors')} *</InputLabel>
            <Select
              labelId='additional-inspectors-label'
              name='general_info.additional_inspectors'
              placeholder={`${t('Additional Auditors')}`}
              label={`${t('Additional Auditors')} *`}
              value={formik?.values?.general_info?.additional_inspectors ?? []}
              renderValue={(selected) => {
                return selected
                  .map(
                    (inspector_id) =>
                      sortedUsers?.find((i) => i._id === (inspector_id as unknown as string))?.name,
                  )
                  .filter(Boolean)
                  .join(', ')
              }}
              onChange={formik.handleChange}
              sx={{ flex: 1 }}
              multiple
            >
              {sortedUsers
                ?.filter((user) => {
                  return (
                    (user?.company as ICompany)?._id === currentCustomer?._id ||
                    (user?.company as ICompany)?._id ===
                      formik?.values?.general_info?.inspection_company?._id
                  )
                })
                .map((inspector) => {
                  return (
                    <MenuItem key={inspector.name} value={inspector._id}>
                      <Checkbox
                        size='small'
                        checked={Boolean(
                          formik.values.general_info?.additional_inspectors?.find(
                            (i) => (i as unknown as string) === inspector._id,
                          ),
                        )}
                      />
                      {inspector.name}
                    </MenuItem>
                  )
                })}
            </Select>
          </FormControl>
          <S.FastField
            component={DatePicker}
            name='general_info.due_date'
            label={t('Due Date')}
            spacing={2}
            openTo='year'
            views={['year', 'month', 'day']}
          />
        </S.InputGroup>
      </S.Section>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          width: '100%',
        }}
      >
        <VinciButton color='primary' onClick={() => navigate(-1)} size='large' variant='outlined'>
          {t('Back')}
        </VinciButton>
        <VinciButton
          color='primary'
          size='large'
          onClick={!editing ? handleClick : handleSave}
          disabled={submitButtonDisabled}
        >
          {editing ? t('Save') : t('Create')}
        </VinciButton>
        <CreateAuditMenu
          onCreate={onCreate}
          onSave={onSave}
          editing={editing}
          anchorEl={anchorEl}
          isCreateAuditMenuOpen={isCreateAuditMenuOpen}
          handleClose={handleClose}
        />
      </Box>
    </>
  )
}
